import React, { useCallback, useEffect, useState } from 'react';

const DateSelection = ({ currentMonth, availability, isAdmin, viewMode, updateAvailability, userEmail, selectedDates, setSelectedDates, serverTime }) => {
  const [forceUpdate, setForceUpdate] = useState(0);
  const [currentServerTime, setCurrentServerTime] = useState(serverTime || new Date());

  // Reset selected dates when the month changes
  useEffect(() => {
    setSelectedDates(new Set());
  }, [currentMonth, setSelectedDates]);

  // Update server time if prop changes
  useEffect(() => {
    if (serverTime) {
      setCurrentServerTime(serverTime);
    }
  }, [serverTime]);

  const handleDateToggle = useCallback((day) => {
    if (viewMode === 'admin') {
      setSelectedDates((prevSelectedDates) => {
        const updatedSet = new Set(prevSelectedDates);
        if (updatedSet.has(day)) {
          updatedSet.delete(day);
        } else {
          updatedSet.add(day);
        }
        return updatedSet;
      });
      setForceUpdate(prev => prev + 1);
    }
  }, [viewMode, setSelectedDates]);

  const handleStatusChange = async (newStatus) => {
    const updates = Array.from(selectedDates).map(day => ({
      date: `${currentMonth.getFullYear()}-${(currentMonth.getMonth() + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`,
      status: newStatus
    }));

    const success = await updateAvailability(updates, userEmail);
    if (success) {
      setSelectedDates(new Set());
    }
  };

  const daysInMonth = React.useMemo(() => {
    const days = [];
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    
    // Get today's date at midnight for comparison (using server time)
    const today = new Date(currentServerTime);
    today.setHours(0, 0, 0, 0);

    // Add empty cells for days before the 1st of the month
    const firstDayOfWeek = date.getDay();
    for (let i = 0; i < (firstDayOfWeek + 6) % 7; i++) {
      days.push({ day: null, status: null });
    }

    // Add the days of the month
    for (let i = 1; i <= lastDay; i++) {
      const key = `${currentMonth.getFullYear()}-${(currentMonth.getMonth() + 1).toString().padStart(2, '0')}-${i.toString().padStart(2, '0')}`;
      const dateForDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i);
      
      // Check if date is in the past
      const isPast = dateForDay < today;
      // For non-admin users, show past dates as not_available. Admins see actual status.
      const actualStatus = availability[key] || 'not_yet_available';
      const status = viewMode === 'user' && isPast ? 'not_available' : actualStatus;
      
      days.push({ day: i, status, isPast });
    }

    return days;
  }, [currentMonth, availability, viewMode, currentServerTime]);

  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <>
      <div className="grid grid-cols-7 gap-2 mb-2">
        {weekdays.map((day, index) => (
          <div key={index} className="text-center font-semibold text-sm text-gray-600">
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2 mb-6">
        {daysInMonth.map(({ day, status }, index) => {
          const isSelected = selectedDates.has(day);
          
          return (
            <button
              key={`${day}-${isSelected}-${forceUpdate}-${index}`}
              onClick={() => day && handleDateToggle(day)}
              className={`
                p-2 md:p-4 text-center rounded-full text-base md:text-lg
                ${!day ? 'invisible' : ''}
                ${day && viewMode === 'user' ? (status === 'available' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800') : 
                  day && status === 'available' ? 'bg-green-100 text-green-800' : 
                  day && status === 'occupied' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}
                ${viewMode === 'admin' ? 'cursor-pointer' : 'cursor-default'}
                ${isSelected ? 'ring-2 ring-blue-500' : ''}
                ${viewMode === 'admin' ? 'focus:outline-none focus:ring-2 focus:ring-blue-500' : ''}
                select-none             
              `}
              disabled={!day || viewMode !== 'admin'}
              aria-label={day ? `${day} ${status}${isSelected ? ', Selected' : ''}` : 'Empty day'}
            >
              {day}
            </button>
          );
        })}
      </div>
      {viewMode === 'admin' && (
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-2 mb-6">
            <button
              onClick={() => handleStatusChange('available')}
              className="flex-1 bg-green-500 text-white py-3 px-6 rounded-md text-lg hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
            >
              Set as Available
            </button>
            <button
              onClick={() => handleStatusChange('occupied')}
              className="flex-1 bg-red-500 text-white py-3 px-6 rounded-md text-lg hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
            >
              Set as Occupied
            </button>
            <button
              onClick={() => handleStatusChange('not_yet_available')}
              className="flex-1 bg-gray-500 text-white py-3 px-6 rounded-md text-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
            >
              Set as Not Yet Available
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default DateSelection;